/**
 * Adaptive quality governor. Measures rendered-frame intervals and moves one step at a time
 * along the quality ladder within the user's preference bounds.
 *
 * Stability rules:
 * - warm-up: samples are ignored for a few seconds after start or any level change;
 * - settling: after the page returns from the background or the graphics are restored, frames
 *   stay slow for a while (the GPU wakes, shaders recompile, iOS hands the page its
 *   time back gradually). Samples are ignored until the frame rate has recovered (a run of frames
 *   within twice the budget), or for at most SETTLE_MAX_MS, so a brief hitch never reads as a
 *   device that cannot cope. Frames that compiled shaders never reach the governor (the host
 *   marks them unsteady);
 * - downgrade only when the 75th percentile frame time stays above budget × 1.2 for two
 *   consecutive windows; when it is severe (under a third of the target rate) skip the cooldown
 *   and, when very severe, drop two levels;
 * - slow frames always count (a frame over a second counts as one second): an overloaded GPU must
 *   never look healthy because only its few quick frames were measured. Gaps that are not
 *   rendering (a hidden tab, a pause) never reach the governor: the host leaves them out;
 * - upgrade only after a long stable period (90th percentile within budget × 1.06 for 30 s),
 *   and never past a level that previously failed within the last few minutes (hysteresis);
 * - cooldowns between any two changes.
 * Frame intervals on vsynced displays cannot reveal spare headroom, so upgrades are cautious
 * probes and a failed probe is remembered.
 */

export interface GovernorOptions {
  minLevel: number;
  maxLevel: number;
  startLevel: number;
  targetFps: number;
  /** Clock in milliseconds (injectable for tests). */
  now?: () => number;
}

export interface GovernorDecision {
  level: number;
  changed: boolean;
  reason?: string;
}

export class QualityGovernor {
  private level: number;
  private samples: number[] = [];
  private windowStart: number;
  private warmupUntil: number;
  private lastChange: number;
  private badWindows = 0;
  private stableSince: number | null = null;
  /** Level → time until which upgrades to it are blocked. */
  private failedUntil = new Map<number, number>();
  private lastUpgradeAt = -Infinity;
  /** Until when samples wait for the frame rate to recover (0: not settling). */
  private settleUntil = 0;
  private settledFrames = 0;
  private now: () => number;
  readonly history: Array<{ t: number; level: number; reason: string }> = [];

  static readonly WARMUP_MS = 4000;
  static readonly WINDOW_MS = 2500;
  static readonly COOLDOWN_MS = 6000;
  static readonly STABLE_FOR_UPGRADE_MS = 30000;
  static readonly FAILED_BLOCK_MS = 5 * 60_000;
  static readonly MAX_SAMPLE_MS = 1000;
  static readonly SETTLE_MAX_MS = 20_000;
  /** Consecutive frames within twice the budget that end settling. */
  static readonly SETTLE_FRAMES = 30;

  constructor(private opts: GovernorOptions) {
    this.now = opts.now ?? (() => performance.now());
    const t = this.now();
    this.level = clampInt(opts.startLevel, opts.minLevel, opts.maxLevel);
    this.windowStart = t;
    this.warmupUntil = t + QualityGovernor.WARMUP_MS;
    this.lastChange = t;
  }

  get current(): number {
    return this.level;
  }

  setBounds(minLevel: number, maxLevel: number, targetFps: number): void {
    this.opts = { ...this.opts, minLevel, maxLevel, targetFps };
    const clamped = clampInt(this.level, minLevel, maxLevel);
    if (clamped !== this.level) this.change(clamped, 'bounds');
  }

  /**
   * Reset measurement. With `settle` (after a return from the background or a graphics restore),
   * also wait for the frame rate to recover before judging.
   */
  restartWarmup(settle = false): void {
    const t = this.now();
    this.samples = [];
    this.windowStart = t;
    this.warmupUntil = t + QualityGovernor.WARMUP_MS;
    this.stableSince = null;
    this.badWindows = 0;
    this.settleUntil = settle ? t + QualityGovernor.SETTLE_MAX_MS : 0;
    this.settledFrames = 0;
  }

  /** Whether samples are still waiting for the frame rate to recover. */
  get settling(): boolean {
    return this.now() < this.settleUntil;
  }

  sample(frameMs: number): GovernorDecision {
    const t = this.now();
    if (t < this.warmupUntil || !Number.isFinite(frameMs) || frameMs <= 0) return { level: this.level, changed: false };
    const budget = 1000 / this.opts.targetFps;
    if (t < this.settleUntil) {
      this.settledFrames = frameMs <= budget * 2 ? this.settledFrames + 1 : 0;
      if (this.settledFrames < QualityGovernor.SETTLE_FRAMES) return { level: this.level, changed: false };
      // Recovered: measure from here.
      this.settleUntil = 0;
      this.samples = [];
      this.windowStart = t;
      return { level: this.level, changed: false };
    }
    this.samples.push(Math.min(frameMs, QualityGovernor.MAX_SAMPLE_MS));
    if (t - this.windowStart < QualityGovernor.WINDOW_MS) return { level: this.level, changed: false };

    const sorted = [...this.samples].sort((a, b) => a - b);
    const p75 = percentile(sorted, 0.75);
    const p90 = percentile(sorted, 0.9);
    this.samples = [];
    this.windowStart = t;

    const coolingDown = t - this.lastChange < QualityGovernor.COOLDOWN_MS;
    if (p75 > budget * 1.2) {
      this.badWindows++;
      this.stableSince = null;
      const severe = p75 > budget * 3;
      if (this.badWindows >= 2 && (severe || !coolingDown) && this.level > this.opts.minLevel) {
        // A failure soon after an upgrade, or a severe one, marks the level as unstable.
        if (severe || t - this.lastUpgradeAt < 20000) this.failedUntil.set(this.level, t + QualityGovernor.FAILED_BLOCK_MS);
        const drop = p75 > budget * 6 ? 2 : 1;
        return this.change(Math.max(this.opts.minLevel, this.level - drop), `p75 ${p75.toFixed(1)}ms > ${(budget * 1.2).toFixed(1)}ms`);
      }
      return { level: this.level, changed: false };
    }
    this.badWindows = 0;
    if (p90 <= budget * 1.06) {
      if (this.stableSince === null) this.stableSince = t;
      const next = this.level + 1;
      const blocked = (this.failedUntil.get(next) ?? 0) > t;
      if (!coolingDown && next <= this.opts.maxLevel && !blocked && t - this.stableSince >= QualityGovernor.STABLE_FOR_UPGRADE_MS) {
        this.lastUpgradeAt = t;
        return this.change(next, `stable p90 ${p90.toFixed(1)}ms`);
      }
    } else {
      this.stableSince = null;
    }
    return { level: this.level, changed: false };
  }

  private change(level: number, reason: string): GovernorDecision {
    const t = this.now();
    this.level = level;
    this.lastChange = t;
    this.warmupUntil = t + QualityGovernor.WARMUP_MS;
    this.samples = [];
    this.windowStart = t;
    this.badWindows = 0;
    this.stableSince = null;
    this.history.push({ t, level, reason });
    if (this.history.length > 50) this.history.shift();
    return { level, changed: true, reason };
  }
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.floor(p * (sorted.length - 1))));
  return sorted[i];
}

function clampInt(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(v)));
}
